import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Shield, Lock, Zap, Globe, Server, Gamepad2, Building2, Wifi,
  KeyRound, ArrowRight, Check, MapPin, Award,
} from "lucide-react";
import GlobeHero from "../components/GlobeHero.jsx";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } };

const features = [
  { icon: Zap, title: "WireGuard protocol", desc: "A modern, lightweight tunnel with strong encryption and a light footprint on every connection." },
  { icon: Shield, title: "No-logs policy", desc: "We do not track, store, or sell your browsing activity. Your traffic is yours alone." },
  { icon: Globe, title: "US WireGuard network", desc: "Route through our own US nodes in Texas, Virginia, and Dallas, with more regions as demand grows." },
  { icon: KeyRound, title: "Consistent egress IP", desc: "Give your team a stable server IP you can allowlist in the tools that matter." },
  { icon: Wifi, title: "Every network, protected", desc: "Encrypt your connection on hotel, cafe, and public Wi-Fi so nothing leaks in transit." },
  { icon: Server, title: "Unlimited devices", desc: "One account covers every laptop, phone, and router your household or team runs." },
];

const steps = [
  { n: "01", title: "Choose a plan", desc: "Personal, gaming, or business. Start a 14-day trial with no card required." },
  { n: "02", title: "Add your device", desc: "Set it up in the dashboard in seconds. Scan a QR code or download a config." },
  { n: "03", title: "Connect", desc: "Pick a node or let us choose the closest one. You are private in under a minute." },
];

const stats = [
  { v: "WireGuard", l: "Modern protocol" },
  { v: "None", l: "Browsing logs" },
  { v: "3", l: "US nodes" },
  { v: "Unlimited", l: "Devices" },
];

const linkClass = "text-brand-300 underline decoration-brand-500/40 underline-offset-2 hover:text-brand-200 transition-colors";

const audienceCards = [
  {
    icon: Building2,
    tag: "For business",
    title: "Secure remote access for your team",
    desc: (
      <>
        Encrypt every connection, give staff a protected path to your tools, and route through a consistent
        IP you can allowlist. Built for <Link to="/industries/home-health" className={linkClass}>home health agencies</Link>,{" "}
        <Link to="/industries/healthcare" className={linkClass}>clinics</Link>,{" "}
        <Link to="/industries/legal" className={linkClass}>legal</Link>, and{" "}
        <Link to="/industries/finance" className={linkClass}>finance</Link> teams handling regulated data off-site.
        See how in our <Link to="/blog/vpn-for-remote-work-security" className={linkClass}>remote work security guide</Link>.
      </>
    ),
    to: "/industries/home-health",
    cta: "Explore business VPN",
  },
  {
    icon: Gamepad2,
    tag: "For gaming",
    title: "Hide your IP, reach any region",
    desc: "Keep your real IP off the table in lobbies and on stream, bypass ISP throttling, and connect to game servers worldwide, all on the low-overhead WireGuard protocol.",
    to: "/gaming",
    cta: "Explore gaming VPN",
  },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>SACVPN - Business VPN for Teams | Private WireGuard Network</title>
        <meta name="description" content="SACVPN is a veteran-owned business VPN powered by WireGuard. Secure remote access for teams, a management dashboard, US-based nodes, and a 14-day free trial with no credit card. Built for healthcare, legal, and financial teams. Personal and gaming plans also available." />
        <meta name="keywords" content="business VPN, secure remote access VPN, team VPN, WireGuard VPN, no-logs VPN, VPN for companies, veteran owned VPN" />
        <link rel="canonical" href="https://www.sacvpn.com/" />
        <meta property="og:title" content="SACVPN - Business VPN for Teams | Private WireGuard Network" />
        <meta property="og:description" content="A private WireGuard network for your team. Secure remote access, no browsing logs, US-based nodes, 14-day free trial." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sacvpn.com/" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden -mt-20 border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.6) 1px,transparent 1px)", backgroundSize: "64px 64px" }} />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 py-24">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-white/5 border border-white/10 mb-7">
                <Zap className="w-3.5 h-3.5 text-brand-400" />
                <span className="text-[13px] tracking-wide text-gray-300">Powered by WireGuard</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-[3.5rem] font-display font-bold text-white leading-[1.05] tracking-tight">
                A private business VPN for{" "}
                <span className="text-brand-400">your team</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="mt-6 text-lg text-gray-400 max-w-xl leading-relaxed">
                SACVPN routes your team's traffic through a private US WireGuard network with a real management dashboard, so remote and hybrid teams stay secure across every device. Personal and gaming plans available too.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link to="/download" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors">
                  Start 14-day free trial <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors">
                  See pricing
                </Link>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-10 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-7 gap-y-3">
                {[
                  { icon: Shield, t: "No-logs policy" },
                  { icon: Lock, t: "WireGuard encryption" },
                  { icon: Globe, t: "US WireGuard network" },
                  { icon: Server, t: "Unlimited devices" },
                  { icon: Award, t: "Veteran-owned" },
                ].map((x, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-400">
                    <x.icon className="w-4 h-4 text-brand-400" />
                    <span className="text-sm">{x.t}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="relative hidden lg:block">
              <div className="relative h-[520px]"><GlobeHero /></div>
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-white/5 backdrop-blur border border-white/10 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                <span className="text-[13px] text-gray-300">3 US nodes · Texas, Virginia, Dallas · live network</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Why SACVPN</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">Built for privacy, speed, and control</h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors">
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <f.icon className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Two audiences: business + gaming */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">One network, two jobs</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">Serious about business. Tuned for gaming.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {audienceCards.map((c, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="group rounded-xl border border-white/[0.08] bg-dark-900 p-8 hover:border-brand-500/30 transition-colors">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <span className="text-xs font-mono uppercase tracking-[0.16em] text-gray-500">{c.tag}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{c.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">{c.desc}</p>
                <Link to={c.to} className="inline-flex items-center gap-2 text-brand-400 font-medium hover:text-brand-300 transition-colors">
                  {c.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Setup</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">Protected in under a minute</h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
                <div className="font-mono text-brand-400 text-sm mb-5">{s.n}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center md:text-left">
                <div className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">{s.v}</div>
                <p className="mt-1.5 text-sm text-gray-500 uppercase tracking-wider">{s.l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">Put your traffic on a private network</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">Protect your team and your gaming with a private WireGuard network. Start your 14-day free trial, no credit card required.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/download" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors">
                Start 14-day free trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors">
                Talk to us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
